import React, { useState, useEffect } from 'react';
import { BulkCreateInput } from './types';
import PolymorphicSelector from './PolymorphicSelector';

interface BulkCreateModalProps {
  apiBaseUrl: string;
  csrfToken: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface PolymorphicOption {
  id: number;
  name: string;
}

const BulkCreateModal: React.FC<BulkCreateModalProps> = ({
  apiBaseUrl,
  csrfToken,
  onClose,
  onSuccess,
}) => {
  const [primaryType, setPrimaryType] = useState<'Skill' | 'Occupation' | 'Characteristic'>('Skill');
  const [primaryId, setPrimaryId] = useState<number | null>(null);
  const [primaryName, setPrimaryName] = useState<string>('');
  const [secondaryType, setSecondaryType] = useState<'Skill' | 'Occupation' | 'Characteristic'>('Occupation');
  const [secondaryNamesText, setSecondaryNamesText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [secondaryOptions, setSecondaryOptions] = useState<PolymorphicOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Secondaryの検索
  useEffect(() => {
    if (searchTerm.length >= 1) {
      const searchEndpoint = secondaryType === 'Skill'
        ? 'search_skills'
        : secondaryType === 'Occupation'
        ? 'search_occupations'
        : 'search_characteristics';

      fetch(`${apiBaseUrl}/${searchEndpoint}?term=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      })
        .then((res) => res.json())
        .then((data: PolymorphicOption[]) => {
          setSecondaryOptions(data);
        })
        .catch(console.error);
    } else {
      setSecondaryOptions([]);
    }
  }, [searchTerm, secondaryType, apiBaseUrl, csrfToken]);

  // Secondary名のリストからIDを解決
  const resolveSecondaryIds = async (names: string[]): Promise<Map<string, number>> => {
    const nameToIdMap = new Map<string, number>();
    const searchEndpoint = secondaryType === 'Skill'
      ? 'search_skills'
      : secondaryType === 'Occupation'
      ? 'search_occupations'
      : 'search_characteristics';

    // 各名前を検索してIDを取得
    for (const name of names) {
      if (!name.trim()) continue;

      try {
        const response = await fetch(`${apiBaseUrl}/${searchEndpoint}?term=${encodeURIComponent(name.trim())}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        });
        const data: PolymorphicOption[] = await response.json();
        
        // 完全一致または部分一致で見つかった最初のものを使用
        const matched = data.find((opt) => 
          opt.name === name.trim() || opt.name.includes(name.trim()) || name.trim().includes(opt.name)
        );
        if (matched) {
          nameToIdMap.set(name.trim(), matched.id);
        }
      } catch (error) {
        console.error(`Failed to resolve ${name}:`, error);
      }
    }

    return nameToIdMap;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors([]);

    if (!primaryId) {
      setErrors(['Primaryを選択してください']);
      setIsSubmitting(false);
      return;
    }

    try {
      // Secondary名のリストを取得（改行区切り）
      const secondaryNames = secondaryNamesText
        .split('\n')
        .map((line) => line.trim())
        .filter((name) => name.length > 0);

      if (secondaryNames.length === 0) {
        setErrors(['Secondaryを1つ以上指定してください']);
        setIsSubmitting(false);
        return;
      }

      // Secondary名からIDを解決
      const nameToIdMap = await resolveSecondaryIds(secondaryNames);
      
      // 解決できなかった名前をチェック
      const unresolvedNames = secondaryNames.filter((name) => !nameToIdMap.has(name));
      if (unresolvedNames.length > 0) {
        setErrors([`以下のSecondaryが見つかりませんでした: ${unresolvedNames.join(', ')}`]);
        setIsSubmitting(false);
        return;
      }

      // Cross Landing Pageのリストを作成
      const inputs: BulkCreateInput[] = secondaryNames.map((name) => {
        const secondaryId = nameToIdMap.get(name)!;
        return {
          primary_type: primaryType,
          primary_id: primaryId,
          secondary_type: secondaryType,
          secondary_id: secondaryId,
          published: false,
          display_priority: 100,
        };
      });

      const response = await fetch(`${apiBaseUrl}/bulk_create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          cross_landing_pages: inputs,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(`${result.created_count}件のCross Landing Pageを作成しました`);
        onSuccess();
      } else {
        setErrors(result.errors || ['一括作成に失敗しました']);
        if (result.error_count > 0) {
          alert(`${result.error_count}件のエラーが発生しました`);
        }
      }
    } catch (error) {
      console.error('Bulk create failed:', error);
      setErrors(['一括作成に失敗しました']);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex={-1}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">一括作成</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="閉じる"
            />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Primary選択 */}
              <div className="mb-4">
                <h6 className="mb-3">Primary（1つ選択）</h6>
                
                <div className="mb-3">
                  <label className="form-label">
                    Primary Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={primaryType}
                    onChange={(e) => {
                      setPrimaryType(e.target.value as 'Skill' | 'Occupation' | 'Characteristic');
                      setPrimaryId(null);
                      setPrimaryName('');
                    }}
                  >
                    <option value="Skill">Skill</option>
                    <option value="Occupation">Occupation</option>
                    <option value="Characteristic">Characteristic</option>
                  </select>
                </div>

                <PolymorphicSelector
                  type={primaryType}
                  value={primaryId}
                  onChange={(id, name) => {
                    setPrimaryId(id);
                    setPrimaryName(name || '');
                  }}
                  apiBaseUrl={apiBaseUrl}
                  csrfToken={csrfToken}
                  label="Primary"
                  required
                />
              </div>

              {/* Secondary選択（複数） */}
              <div className="mb-4">
                <h6 className="mb-3">Secondary（複数選択可能）</h6>
                
                <div className="mb-3">
                  <label className="form-label">
                    Secondary Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={secondaryType}
                    onChange={(e) => {
                      setSecondaryType(e.target.value as 'Skill' | 'Occupation' | 'Characteristic');
                      setSecondaryNamesText('');
                    }}
                  >
                    <option value="Skill">Skill</option>
                    <option value="Occupation">Occupation</option>
                    <option value="Characteristic">Characteristic</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Secondary名（1行に1つ） <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={8}
                    value={secondaryNamesText}
                    onChange={(e) => setSecondaryNamesText(e.target.value)}
                    placeholder={`例:\nフロントエンドエンジニア\nバックエンドエンジニア\n在宅・リモート\n高単価`}
                  />
                  <small className="form-text text-muted">
                    1行に1つのSecondary名を入力してください。名前から自動的にIDを検索します。
                  </small>
                </div>

                {/* 検索候補表示（オプション） */}
                {searchTerm && secondaryOptions.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">検索候補</label>
                    <div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {secondaryOptions.map((option) => (
                        <div
                          key={option.id}
                          className="px-2 py-1 cursor-pointer hover-bg-light"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const currentNames = secondaryNamesText.split('\n').filter((n) => n.trim());
                            if (!currentNames.includes(option.name)) {
                              setSecondaryNamesText([...currentNames, option.name].join('\n'));
                            }
                            setSearchTerm('');
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                          }}
                        >
                          {option.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 検索ボックス（オプション） */}
                <div className="mb-3">
                  <label className="form-label">検索して追加</label>
                  <input
                    type="text"
                    className="form-control"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`${secondaryType}名で検索...`}
                  />
                </div>
              </div>

              {/* プレビュー */}
              {primaryId && primaryName && secondaryNamesText.trim() && (
                <div className="mb-4">
                  <h6 className="mb-3">プレビュー</h6>
                  <div className="bg-light p-3 rounded">
                    <p className="mb-2">
                      <strong>作成されるCross Landing Page:</strong>
                    </p>
                    <ul className="mb-0">
                      {secondaryNamesText
                        .split('\n')
                        .map((name) => name.trim())
                        .filter((name) => name.length > 0)
                        .map((name, index) => (
                          <li key={index}>
                            {primaryName} × {name}
                          </li>
                        ))}
                    </ul>
                    <p className="mb-0 mt-2 text-muted">
                      <small>合計: {secondaryNamesText.split('\n').filter((n) => n.trim()).length}件</small>
                    </p>
                  </div>
                </div>
              )}

              {errors.length > 0 && (
                <div className="alert alert-danger">
                  <ul className="mb-0">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? '作成中...' : '作成'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkCreateModal;
